import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState<string>(slug || 'character-relationship');
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    setVisibleCount(6);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === activeSlug);
  const visibleArticles = categoryArticles.slice(0, visibleCount);
  const hasMore = visibleCount < categoryArticles.length;

  return (
    <div className="pt-20 min-h-screen bg-background-50">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: currentCategory.name }]} />

      {/* Category header */}
      <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
            <i className={`${currentCategory.icon} text-2xl`}></i>
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{currentCategory.name}</h1>
            <p className="text-sm text-foreground-500 mt-1">{currentCategory.articleCount} 篇文章</p>
          </div>
        </div>
        <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">{currentCategory.description}</p>
      </div>

      <div className="w-full px-4 md:px-6 lg:px-8 pb-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-background-100 rounded-lg p-4 lg:sticky lg:top-24">
              <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-3">创作分类</h4>
              <nav className="space-y-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-accent-100 text-accent-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    <span className={`w-7 h-7 flex items-center justify-center rounded ${
                      activeSlug === cat.slug ? 'text-accent-600' : 'text-foreground-400'
                    }`}>
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="flex-1 whitespace-nowrap">{cat.name}</span>
                    <span className="text-xs text-foreground-400">{cat.articleCount}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Article list */}
          <main className="flex-1 min-w-0">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  {visibleArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-44 md:h-48 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-accent-600 transition-colors duration-200 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <div className="flex items-center gap-3">
                            <span>{article.author}</span>
                            <span>{article.publishDate}</span>
                          </div>
                          <span>{article.readTime} 分钟阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {hasMore && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 6)}
                      className="px-8 py-3 rounded-full border border-background-300 text-foreground-600 text-sm font-medium hover:bg-background-100 hover:border-accent-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      加载更多 <i className="ri-arrow-down-s-line ml-1"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}